import { useTranslation } from 'react-i18next';
import { Link as LinkIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import OrganisationBreadCrumb from 'shared/components/bread-crumb/organisation-bread-crumb/organisation-bread-crumb';
import PopUp from 'shared/components/pop-up/pop-up';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';
import useGatewayOrganisation from 'features/gateway/hooks/gateway-settings-organisation-hook';
import styles from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-organisation.module.scss';
import OrganisationPicker from 'features/organisation/views/organisation-picker/organisation-picker';

const GatewaySettingsOrganisation = () => {
  const { t } = useTranslation('gateway');
  const { gatewayDetailsHook } = useGatewaysContextProvider();
  const {
    isLinkErrorPopupOpen,
    isOrganisationPickerOpen,
    upstreamParentBranch,
    organisationUnitField,
    setOrganisationUnit,
    linkOrganisationUnit,
    unlinkOrganisationUnit,
    closeLinkErrorPopup,
    closeOrganisationPicker,
  } = useGatewayOrganisation();

  const isEditing = gatewayDetailsHook.viewingMode === 'editing';
  const isCreating = gatewayDetailsHook.viewingMode === 'creation';
  const showActionButtons =
    (isEditing || isCreating) && gatewayDetailsHook.selectedSystemType !== GatewayType.IoTEdgeMediaServerGatewayCluster;

  const organisationUnitId = showActionButtons
    ? organisationUnitField.value
    : gatewayDetailsHook.currentSelectedGateway?.organisationUnitId;

  const hasOrganisationUnit = organisationUnitId != null;
  const gatewayName = gatewayDetailsHook.currentSelectedGateway?.name ?? '';

  return (
    <>
      {hasOrganisationUnit ? (
        <div className={styles.container}>
          <OrganisationBreadCrumb rootNodeId={organisationUnitId} upstreamParentBranch={upstreamParentBranch} />
        </div>
      ) : (
        <Typography variant="subtitle1" sx={{ whiteSpace: 'pre-line' }}>
          {t('details.settings.noOrganisationUnitLinkedLabel')}
        </Typography>
      )}

      {showActionButtons &&
        (hasOrganisationUnit ? (
          <Button variant="outlined" onClick={unlinkOrganisationUnit} startIcon={<LinkOffIcon />} data-testid="unlinkOrganisationButton">
            {t('details.settings.unlinkOrganisationUnitButton')}
          </Button>
        ) : (
          <Button variant="outlined" onClick={linkOrganisationUnit} startIcon={<LinkIcon />} data-testid="linkOrganisationButton">
            {t('details.settings.linkOrganisationUnitButton')}
          </Button>
        ))}

      <OrganisationPicker
        isOpen={isOrganisationPickerOpen}
        onClose={closeOrganisationPicker}
        onOrganisationUnitSelected={(selectedNode, upstreamParentBranch) =>
          setOrganisationUnit(organisationUnitField, selectedNode, upstreamParentBranch)
        }
        sessionStorageKey={FilterSessionStorageKeys.GATEWAY_ORGANISATION_PICKER_EXPANDED_NODES}
      />

      <PopUp
        isOpen={isLinkErrorPopupOpen}
        title={t('linkGatewayError.title', { gatewayName })}
        body={t('linkGatewayError.bodyText', { gatewayName })}
        primaryButtonText={t('linkGatewayError.confirmButton')}
        handleOnClose={closeLinkErrorPopup}
        primaryButtonAction={closeLinkErrorPopup}
      />
    </>
  );
};

export default GatewaySettingsOrganisation;
